/**
 * Kết quả thao tác hàng loạt: BA nhóm bắt buộc theo mục 3.1 đặc tả giao diện —
 * thành công, đang chờ, lỗi. Nhóm lỗi liệt kê được kèm lý do và thử lại được RIÊNG.
 *
 * Vì sao phải tách ba nhóm: lệnh gửi cho 50 thiết bị mà 8 thiết bị mất kết nối thì
 * báo "đã gửi" là sai, còn báo "thất bại" cũng sai. Người vận hành cần biết CHÍNH
 * XÁC thiết bị nào chưa nhận được để đi xử lý, và chỉ thử lại đúng những thiết bị đó.
 */
import type { ReactNode } from 'react';
import { dienVao, vi } from '../ngon-ngu/vi';
import type { KetQuaHangLoat as DuLieuKetQua } from '../api/thiet-bi-api';

export interface BangKetQuaHangLoatProps {
  ketQua: DuLieuKetQua;
  dangThuLai?: boolean;
  /** Nhận ĐÚNG danh sách id ở nhóm lỗi; không bao giờ gửi lại cả lô. */
  onThuLaiPhanLoi?: (deviceIds: string[]) => void;
  onDong?: () => void;
}

export function BangKetQuaHangLoat({
  ketQua,
  dangThuLai,
  onThuLaiPhanLoi,
  onDong,
}: BangKetQuaHangLoatProps): ReactNode {
  const idLoi = ketQua.loi.map((x) => x.deviceId);
  return (
    <section className="ket-qua-lo" data-vung-ket-qua="hang-loat" aria-live="polite">
      <h3>{vi.ketQuaLo.tieuDe}</h3>
      <ul className="ket-qua-lo__so">
        <li data-nhom="thanh-cong">
          <span className="ket-qua-lo__nhan">{vi.ketQuaLo.thanhCong}</span>
          <strong data-so="thanh-cong">{ketQua.thanhCong.length}</strong>
        </li>
        <li data-nhom="dang-cho">
          <span className="ket-qua-lo__nhan">{vi.ketQuaLo.dangCho}</span>
          <strong data-so="dang-cho">{ketQua.dangCho.length}</strong>
        </li>
        <li data-nhom="loi">
          <span className="ket-qua-lo__nhan">{vi.ketQuaLo.loi}</span>
          <strong data-so="loi">{ketQua.loi.length}</strong>
        </li>
      </ul>

      {/* Nhóm LỖI đặt TRƯỚC nhóm đang chờ: đây là phần người vận hành phải xử
          lý, không được để danh sách "đang chờ" dài đẩy nó xuống dưới màn hình. */}
      {ketQua.loi.length > 0 ? (
        <div className="ket-qua-lo__loi">
          <h4>{vi.ketQuaLo.danhSachLoi}</h4>
          <table>
            <thead>
              <tr>
                <th scope="col">{vi.ketQuaLo.maThietBi}</th>
                <th scope="col">{vi.ketQuaLo.maLoi}</th>
                <th scope="col">{vi.ketQuaLo.lyDo}</th>
              </tr>
            </thead>
            <tbody>
              {ketQua.loi.map((x) => (
                <tr key={x.deviceId}>
                  <td>
                    <code>{x.cumLoaId}</code>
                  </td>
                  <td>
                    <code>{x.maLoi}</code>
                  </td>
                  <td>{x.moTaLoi}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ket-qua-lo__ghi-chu">{vi.ketQuaLo.ghiChuThuLai}</p>
          {onThuLaiPhanLoi ? (
            <button
              type="button"
              className="nut nut--chinh"
              disabled={dangThuLai}
              data-hanh-dong="thu-lai-loi"
              onClick={() => onThuLaiPhanLoi(idLoi)}
            >
              {dangThuLai ? vi.ketQuaLo.dangThuLai : dienVao(vi.ketQuaLo.thuLaiPhanLoi, { so: idLoi.length })}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Nhóm đang chờ thu gọn: chỉ là thông tin theo dõi, không cần thao tác. */}
      {ketQua.dangCho.length > 0 ? (
        <details className="ket-qua-lo__cho">
          <summary>
            {vi.ketQuaLo.dangCho}: {ketQua.dangCho.length}
          </summary>
          <ul className="ket-qua-lo__moc">
            {ketQua.dangCho.map((x) => (
              <li key={x.deviceId}>
                <code>{x.cumLoaId}</code> —{' '}
                {vi.dieuKhien.moc[x.moc as keyof typeof vi.dieuKhien.moc] ?? x.moc}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {onDong ? (
        <button type="button" className="nut" onClick={onDong}>
          {vi.ketQuaLo.dong}
        </button>
      ) : null}
    </section>
  );
}
